import { useRef } from "react";
import { Stack, TextField } from "@mui/material";
import { t } from "../../../locales/t";

/**
 * Four single-character inputs for the emailed verification code.
 *
 * Behaviour is deliberately identical to `src/modules/Auth/EnterSecurityCode`,
 * which carries the paste fix shipped in March 2026 (Trello echo-77): pasting a
 * code used to land entirely in the first box, so anyone copying from their
 * email client was stuck. The regression matters more than the feature.
 *
 * Written fresh rather than extracted from that component, on purpose. That one
 * is 407 lines wired into the Redux auth flow that live signup depends on, and
 * refactoring it inside an onboarding ticket risks the working signup to save
 * duplicating forty lines. Once the redesign replaces the old flow, this is the
 * component that survives and that one is deleted.
 *
 * @param {object} props
 * @param {string[]} props.value      one character per box
 * @param {(next: string[]) => void} props.onChange
 * @param {boolean} [props.error]
 */
const OtpInput = ({ value, onChange, error, length = 4 }) => {
  const refs = useRef([]);

  const setDigit = (index, digit) => {
    const next = [...value];
    next[index] = digit;
    onChange(next);
    return next;
  };

  const handleChange = (index) => (event) => {
    const digit = event.target.value.replace(/\D/g, "").slice(-1);
    setDigit(index, digit);

    // Advance only on entry. Advancing on deletion too would make backspace
    // walk forwards, which is the sort of thing that feels broken without
    // being obviously wrong.
    if (digit && index < length - 1) {
      refs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index) => (event) => {
    if (event.key !== "Backspace") return;

    // Only step back from an already-empty box, so the first backspace clears
    // the character you can see rather than jumping away from it.
    if (!value[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();

    // Stripped, not validated: codes get copied out of email clients with
    // spaces, hyphens or a trailing newline attached, and none of that is the
    // member doing anything wrong.
    const digits = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, length);

    if (!digits) return;

    const next = Array.from({ length }, (_, i) => digits[i] ?? "");
    onChange(next);

    // Land on the first empty box, or the last one for a complete code.
    refs.current[Math.min(digits.length, length - 1)]?.focus();
  };

  return (
    <Stack direction="row" gap={1.5} justifyContent="center">
      {Array.from({ length }, (_, index) => (
        <TextField
          key={index}
          value={value[index] ?? ""}
          onChange={handleChange(index)}
          onKeyDown={handleKeyDown(index)}
          onPaste={handlePaste}
          error={error}
          inputRef={(el) => (refs.current[index] = el)}
          slotProps={{
            htmlInput: {
              // On the input, NOT on the TextField. MUI spreads unrecognised
              // props onto the root FormControl, so an `aria-label` passed to
              // TextField lands on a wrapper div: the input keeps no accessible
              // name at all, and a screen reader announces four unlabelled
              // boxes. It looks correct in the JSX and is silently wrong.
              "aria-label": t("onboarding.verifyEmail.digitLabel", {
                position: index + 1,
              }),
              maxLength: 1,
              // Brings up the numeric keypad on a phone rather than the full
              // keyboard, which is the difference between four taps and a
              // fight.
              inputMode: "numeric",
              pattern: "[0-9]*",
              style: { textAlign: "center", fontSize: "1.5rem" },
            },
          }}
          sx={{ width: 60 }}
        />
      ))}
    </Stack>
  );
};

export default OtpInput;
